import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useGlobalManualLocationOrder } from "../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { dailyViewStaffTableSortingKey } from "../../services/LocalStorageService";
import type { Nurse, NurseFullRole, NurseInShift, OpenShiftRequest } from "../../utils/models";
import { handleColumnSortClick, multiSortComparator, type SortingState } from "../../utils/multiColumnSorting";
import type { MultiSortOption } from "../UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { ActualGroupedTableBody } from "./ActualGroupedTableBody";
import { NursesTableBody } from "./NursesTableBody";
import { PtoTableBody } from "./PtoTableBody";
import { RequestsTableBody } from "./RequestsTableBody";
import { getDeltaHoursByNurseId, getStaffTableColumnWidths } from "./utils";

type StaffTableProps = {
	showActual: boolean;
	staff: NurseInShift[];
	date: DateTime;
	requests: OpenShiftRequest[];
	sortState: SortingState<NurseInShiftWithWage>[];
	setSortState: Dispatch<SetStateAction<SortingState<NurseInShiftWithWage>[]>>;
	onSortHandledFromInside: () => void;
	invalidNurses?: NurseInShift[];
	recentCallInsCounts?: Record<number, number>;
	selectedNurse?: Nurse | null;
	selectedTimeRange?: { start: DateTime; end: DateTime };
	selectedRoles?: NurseFullRole[] | "All";
};

export type NurseInShiftWithWage = NurseInShift & { wage: number; shiftDeltaHours?: number };
export const staffTableColumns: MultiSortOption<NurseInShiftWithWage>[] = [
	{ label: "Role", sortColumns: ["role"] },
	{ label: "Job", sortColumns: ["jobName"] },
	{ label: "Name", sortColumns: ["name"] },
	{ label: "Shift", sortColumns: ["startTime", "endTime"] },
	{ label: "Location", sortColumns: ["location"] },
	{ label: "Shift Delta", sortColumns: ["shiftDeltaHours"] },
	{ label: "Notes" },
	{ label: "Action" },
];
export const defaultSortStateStaffTable: SortingState<NurseInShiftWithWage>[] = [
	{
		sortColumns: ["startTime", "endTime"],
		label: "shift",
		sortOrder: "asc",
	},
	{
		sortColumns: ["role"],
		sortOrder: "asc",
	},
	{
		sortColumns: ["location"],
		sortOrder: "asc",
	},
	{
		sortColumns: ["name"],
		sortOrder: "asc",
	},
	{
		sortColumns: ["id"],
		sortOrder: "asc",
	},
] as const satisfies SortingState<NurseInShiftWithWage>[];

export const StaffTable: FC<StaffTableProps> = ({
	showActual,
	staff,
	requests,
	date,
	sortState,
	setSortState,
	onSortHandledFromInside,
	invalidNurses,
	recentCallInsCounts,
	selectedNurse,
	selectedTimeRange,
	selectedRoles,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalDemo, isHospitalView } = useDepartmentFlags();
	const showHospitalView = isHospitalView || isHospitalDemo;
	const {
		userInfo: { id: userId },
	} = useAuth();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const { weekViewData, getSchedulePerson, isShiftSalaried } = useWeekViewContext();
	const navigate = useNavigate();
	const location = useLocation();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const shiftDeltaHoursByNurseId = useMemo<Map<number, number>>(() => {
		if (!showActual) return new Map();
		const scheduledShifts = weekViewData[date.weekdayISO]?.shifts ?? [];
		return getDeltaHoursByNurseId({
			staff,
			scheduledShifts,
			department: chosenDepartment,
			jobManagementEnabled,
			getPerson: getSchedulePerson,
			isSalaried: isShiftSalaried,
		});
	}, [showActual, weekViewData, date, staff, chosenDepartment, jobManagementEnabled, getSchedulePerson, isShiftSalaried]);

	// First time the user adds Shift Delta to the picker it lands as "asc" (multiSortComparator default).
	// Flip it to "desc" once so "biggest first" is the natural starting point — they can still toggle.
	const hadShiftDeltaSort = useRef(false);
	useEffect(() => {
		const entry = sortState.find((s) => s.sortColumns.includes("shiftDeltaHours"));
		const hasIt = !!entry;
		if (hasIt && !hadShiftDeltaSort.current && entry.sortOrder === "asc") {
			setSortState((prev) => prev.map((s) => (s.sortColumns.includes("shiftDeltaHours") ? { ...s, sortOrder: "desc" as const } : s)));
		}
		hadShiftDeltaSort.current = hasIt;
	}, [sortState, setSortState]);

	const shiftDeltaSortOrder: "asc" | "desc" = sortState.find((s) => s.sortColumns.includes("shiftDeltaHours"))?.sortOrder ?? "desc";

	const onDoubleClickNurse = useCallback(
		(nurseId: number) => {
			navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`, {
				state: { navigationSrc: location.pathname },
			});
		},
		[navigate, chosenDepartment.department_id, location.pathname],
	);

	const columns = useMemo<MultiSortOption<NurseInShiftWithWage>[]>(() => {
		const baseColumns: MultiSortOption<NurseInShiftWithWage>[] = [
			{ label: "Role", sortColumns: ["role"] },
			{ label: "Job", sortColumns: ["jobName"] },
			{ label: "Name", sortColumns: ["name"] },
			{ label: "Shift", sortColumns: ["startTime", "endTime"] },
			{ label: "Location", sortColumns: ["location"] },
		];

		if (showHospitalView) baseColumns.push({ label: "# of Patients" }, { label: "Total Workload" });

		baseColumns.push({ label: "Notes" }, { label: "Action" });

		return baseColumns;
	}, [showHospitalView]);

	const sortedStaff = useMemo(() => {
		if (!sortState) return staff;
		return staff
			.map<NurseInShiftWithWage>((nurseInShift) => ({
				...nurseInShift,
				wage: nurseInShift.cost ?? 0,
				shiftDeltaHours: shiftDeltaHoursByNurseId.get(nurseInShift.nurseId) ?? 0,
			}))
			.sort(multiSortComparator(sortState, customStringOrderMap));
	}, [staff, sortState, customStringOrderMap, shiftDeltaHoursByNurseId]);

	const sortedInvalidNurses = useMemo(() => {
		if (!sortState || sortState.length === 0 || !invalidNurses) return invalidNurses;
		return [...invalidNurses]
			.map((nurseInShift) => ({
				...nurseInShift,
				wage: nurseInShift.cost ?? 0,
				shiftDeltaHours: shiftDeltaHoursByNurseId.get(nurseInShift.nurseId) ?? 0,
			}))
			.sort(multiSortComparator(sortState, customStringOrderMap));
	}, [invalidNurses, sortState, customStringOrderMap, shiftDeltaHoursByNurseId]);

	const sortedRequests = useMemo(() => {
		if (!sortState || sortState.length === 0) return requests;

		return [...requests].sort((a, b) => {
			if (sortState.some((specificState) => specificState.sortColumns.includes("startTime"))) {
				return a.message.shift_start.valueOf().toString().localeCompare(b.message.shift_start.valueOf().toString());
			}
			return 0;
		});
	}, [requests, sortState]);

	const handleColumnSortClicked = useCallback(
		(label?: string, columns?: Array<keyof NurseInShiftWithWage>) => {
			handleColumnSortClick({
				setSortState,
				sortState,
				columns,
				label,
				appendToStartIfAlreadyExists: true,
				allowRemovalWhenDescendingToggle: false,
				localStorageDetails: {
					localStorageTableKey: dailyViewStaffTableSortingKey,
					userId,
				},
			});
			onSortHandledFromInside();
		},
		[setSortState, sortState, onSortHandledFromInside, userId],
	);

	return (
		<TableContainer sx={{ overflow: "auto" }}>
			<Table stickyHeader>
				<TableHead>
					<TableRow
						sx={{
							position: "relative",
							fontSize: "14px",
							"& th": {
								px: 1.25,
								py: 1.5,
								position: "sticky",
								top: 0,
								zIndex: 1,
								color: "primary.dark",
								borderBottom: "2px solid #E7E9EF",
								bgcolor: "white",
							},
						}}
					>
						{columns.map((column) => {
							const foundState = sortState.find((sState) => column.sortColumns?.every((col) => sState.sortColumns.includes(col)));

							const columnWidths = getStaffTableColumnWidths(showHospitalView);
							const labelToKey: Record<string, keyof ReturnType<typeof getStaffTableColumnWidths>> = {
								Role: "role",
								Job: "job",
								Name: "name",
								Shift: "shift",
								Location: "location",
								"# of Patients": "patients",
								"Total Workload": "workload",
								Notes: "notes",
								Action: "action",
							};
							const widthKey = labelToKey[column.label];
							const colWidth = widthKey ? columnWidths[widthKey] : { width: "auto" };

							const isNumericDemoCol = column.label === "# of Patients" || column.label === "Total Workload";
							return (
								<TableCell
									key={column.label}
									sx={{
										width: colWidth.width,
										minWidth: colWidth.minWidth,
										textAlign: isNumericDemoCol ? "center" : "left",
										...(column.label === "Job" ? { pl: 2 } : {}),
										...(column.label === "Name" ? { pl: 2 } : {}),
									}}
								>
									{column.sortColumns ? (
										<TableSortLabel
											active={!!foundState}
											direction={foundState?.sortOrder}
											onClick={(e) => {
												e.stopPropagation();
												handleColumnSortClicked(column.label, column.sortColumns);
											}}
											sx={{
												fontSize: "14px",
												fontWeight: 600,
												color: "primary.dark",
												"&.Mui-active": {
													color: "primary.dark",
												},
												"& .MuiTableSortLabel-icon": {
													color: "#4D5579 !important",
												},
											}}
										>
											{column.label}
										</TableSortLabel>
									) : (
										<Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{column.label}</Typography>
									)}
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{showActual ? (
						<ActualGroupedTableBody
							staff={sortedStaff}
							invalidNurses={sortedInvalidNurses ?? []}
							date={date}
							shiftDeltaSortOrder={shiftDeltaSortOrder}
							onDoubleClickNurse={onDoubleClickNurse}
							selectedNurseId={selectedNurse ? selectedNurse.id : null}
						/>
					) : (
						<>
							<RequestsTableBody date={date} requests={sortedRequests} />
							<NursesTableBody
								date={date}
								staff={sortedStaff}
								showActual={showActual}
								recentCallInsCounts={recentCallInsCounts}
								selectedTimeRange={selectedTimeRange}
								selectedRoles={selectedRoles}
								isStaffChosen={!!selectedNurse}
							/>
						</>
					)}
					<PtoTableBody date={date} onDoubleClickNurse={onDoubleClickNurse} selectedNurse={selectedNurse} />
				</TableBody>
			</Table>
		</TableContainer>
	);
};
